import { useState, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { IProduct } from "$containers";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";

export const useMaterialNeed = () => {
  const { watch, setValue } = useFormContext();

  const [productError, setProductError] = useState<string>("");
  const [showEmptyCartModal, setShowEmptyCartModal] = useState(false);
  const [product, setProduct] = useState<IProduct | undefined>(undefined);

  const cart: any[] = watch("products", []);
  const integratedOrders: EDBIntegratedOrders = watch("integratedOrders");
  const dockyardId: string = watch("dockyard");

  const handleClickProduct = useCallback((_product: IProduct) => {
    setProduct(_product);
  }, []);

  const addToCart = useCallback(
    (pID: string, qty: number, replace = true, completeProduct?: any) => {
      const product = cart.find(({ productID }) => productID === pID);
      if (product) {
        if (replace) {
          product.qty = qty;
        } else {
          product.qty += qty;
        }
        if (product.qty <= 0) {
          setValue(
            "products",
            cart.filter((p) => p.productID !== pID)
          );
        } else {
          setValue(
            "products",
            cart.map((p) => ({ ...p }))
          );
        }
      } else if (completeProduct) {
        cart.push({ ...completeProduct, productID: pID, qty });
        setValue(
          "products",
          cart.map((p) => ({ ...p }))
        );
      } else {
        cart.push({ productID: pID, qty });
        setValue(
          "products",
          cart.map((p) => ({ ...p }))
        );
      }
    },
    [cart, setValue]
  );

  const removeFromCart = useCallback(
    (product: any) => {
      setValue(
        "products",
        cart.filter((p) => p.productID !== product)
      );
    },
    [cart, setValue]
  );

  const setIntegratedOrders = useCallback(
    (integratedOrders: EDBIntegratedOrders) => {
      setValue("integratedOrders", integratedOrders);
    },
    [setValue]
  );

  const clearCart = useCallback(() => {
    setValue("products", []);
  }, [setValue]);

  const getTotalArticle = useCallback(() => {
    const integratedTotal = integratedOrders
      ? integratedOrders?.orderProducts?.reduce(
          (acc, product) => product.quantity + acc,
          0
        ) ?? 0
      : 0;
    const cartTotal = cart?.reduce((acc, product) => product.qty + acc, 0);
    return integratedTotal + cartTotal;
  }, [cart, integratedOrders]);

  const handleEmptyCart = useCallback(() => {
    clearCart();
    setIntegratedOrders({
      ...integratedOrders,
      orderProducts: [],
    });
    setShowEmptyCartModal(false);
  }, [clearCart, integratedOrders, setIntegratedOrders]);

  return {
    cart,
    integratedOrders,
    dockyardId,
    productError,
    setProductError,
    showEmptyCartModal,
    setShowEmptyCartModal,
    product,
    setProduct,
    handleClickProduct,
    addToCart,
    removeFromCart,
    setIntegratedOrders,
    clearCart,
    getTotalArticle,
    handleEmptyCart,
  };
};
